import clsx from 'clsx';
import * as React from 'react';

import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { Chip, Description, Label, type TextInputProps } from '@gitbook/spine-react';
import { useEventCallback, useStateWithDeps } from '@gitbook/util-react-hooks';

export type TagInputProps<TagValue = string> = Omit<
    React.HTMLAttributes<HTMLInputElement>,
    'value' | 'onChange'
> & {
    label?: React.ReactNode;
    labelDescription?: React.ReactNode;
    labelSelectable?: boolean;
    value: TagValue[];
    text?: string;
    placeholder?: string;
    onChangeText?: (text: string) => void;
    onEnterTag?: (text: string) => void;
    onChangeValue: (tags: TagValue[]) => void | React.Dispatch<React.SetStateAction<TagValue[]>>;
    renderTag?: (tag: TagValue) => string;
    disabled?: boolean;
    feedback?: TextInputProps['feedback'];
    /** Key codes that will trigger the onEnterTag callback. */
    tagDelimiters?: string[];
    /** Button to show on the right side of the input */
    button?: React.ReactNode;
    /** React ref to the wrapper view. */
    wrapperRef?: React.Ref<HTMLDivElement>;
    /** Classes to apply to the container */
    className?: string;
};

/**
 * A hook that can be used to take into account the remaining of the text in the TagInput
 * It returns the props to set on the TagInput as well as the value + the text (if validated)
 */
export function useTagInputState<TagValue = string>(
    options: {
        validateTag?: (newText: string) => boolean;
        maxTags?: number;
        getTag?: (newText: string) => TagValue;
        initialState?: TagValue[];

        // When used in a controlled way
        value?: TagValue[];
        onChangeValue?: React.Dispatch<React.SetStateAction<TagValue[]>>;
    } = {}
) {
    const {
        validateTag = (newText: string) => Boolean(newText),
        maxTags,
        getTag = (newText: string) => newText as TagValue,
        initialState = [],
        value: controlledValue,
        onChangeValue: onChangeControlled,
    } = options;
    const [valueUncontrolled, onChangeUncontrolled] = useStateWithDeps<TagValue[]>(
        initialState,
        initialState
    );

    const value = controlledValue ?? valueUncontrolled;
    const setValue = useEventCallback(onChangeControlled ?? onChangeUncontrolled);

    const [text, setText] = React.useState('');
    const [feedback, setFeedback] = React.useState<TextInputProps['feedback'] | undefined>();

    const onSaveTags = React.useCallback(
        (newTags: TagValue[]) => {
            setFeedback(undefined);

            if (maxTags && newTags.length > maxTags) {
                setFeedback({
                    type: 'danger',
                    message: `Too many items entered, please enter at most ${maxTags} items.`,
                });
                return;
            }

            if (newTags) {
                setValue(newTags);
            }

            setText('');
        },
        [setValue, maxTags]
    );

    const addTags = React.useCallback(
        (tags: TagValue[]) => {
            setValue((value) => {
                if (maxTags && value.length + tags.length > maxTags) {
                    setFeedback({
                        type: 'danger',
                        message: `Too many items entered, please enter at most ${maxTags} items.`,
                    });
                    return value;
                }

                return value.concat(tags);
            });
        },
        [setValue, maxTags]
    );

    const onEnterTag = React.useCallback(
        (newText: string) => {
            setFeedback(undefined);

            if (validateTag(newText)) {
                addTags([getTag(newText)]);
            }

            setText('');
        },
        [validateTag, addTags, getTag]
    );

    const onChangeText = React.useCallback(
        (input: string) => {
            // Handle potentially pasted content
            const parts = input.split(/[\s,;]+/);
            const tags: TagValue[] = [];

            const newText = parts.pop() || '';

            for (const part of parts) {
                if (validateTag(part)) {
                    tags.push(getTag(part));
                }
            }

            if (tags.length) {
                addTags(tags);
            }

            setText(newText);
        },
        [validateTag, getTag, addTags]
    );

    const valueAndRemainingText = validateTag?.(text) ? value.concat([getTag(text)]) : value;
    const reset = () => {
        setValue(initialState);
        setText('');
        setFeedback(undefined);
    };

    return [
        { onChangeValue: onSaveTags, value, feedback, text, onChangeText, onEnterTag },
        valueAndRemainingText,
        { reset },
    ] as const;
}

export type TagInputRef = HTMLInputElement;

const defaultTagDelimiters = [',', ' ', ';', 'Enter', 'Tab'];

/**
 * Input to insert tags.
 */
export function TagInput<TagValue = string>({
    ref,
    ...props
}: TagInputProps<TagValue> & { ref?: React.Ref<TagInputRef> }): React.ReactElement {
    const [defaultText, setDefaultText] = React.useState('');
    const {
        label,
        labelDescription,
        labelSelectable,
        value,
        text = defaultText,
        style,
        onChangeValue,
        onChangeText = setDefaultText,
        onEnterTag = (newText: string) => {
            if (newText) {
                // @ts-ignore
                props.onChangeValue(value.concat([newText]));
            }

            onChangeText('');
        },
        renderTag = (tag: TagValue) => String(tag),
        disabled,
        placeholder,
        feedback,
        wrapperRef,
        button,
        tagDelimiters = defaultTagDelimiters,
        className,
        ...inputProps
    } = props;
    const [focused, setFocused] = React.useState(false);

    const onRemoveTag = (index: number) => {
        if (disabled) {
            return;
        }
        const newValue = [...value];
        newValue.splice(index, 1);
        onChangeValue(newValue);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        if (value.length > 0 && text.length === 0 && event.key === 'Backspace') {
            onRemoveTag(value.length - 1);
            return;
        }

        if (onEnterTag && tagDelimiters.includes(event.key)) {
            onEnterTag(text);

            if (text) {
                // prevent bluring the input when pressing Enter or Tab
                event.preventDefault();
            }
        }
    };

    let FeedbackIcon: SVGIconComponent | undefined;
    let feedbackStyle: string | undefined;
    if (feedback) {
        if (feedback.type === 'success') {
            FeedbackIcon = Icon.Check;
            feedbackStyle = 'text-success';
        } else if (feedback.type === 'info') {
            feedbackStyle = 'text-primary';
        } else if (feedback.type === 'danger') {
            FeedbackIcon = Icon.Alert;
            feedbackStyle = 'text-danger';
        }
    }

    return (
        <div className={clsx('flex flex-col', className)}>
            {label ? (
                <div className="mb-2">
                    <Label className={labelSelectable ? 'select-all' : undefined}>{label}</Label>
                    {labelDescription ? (
                        <div className="mt-1">
                            <Description className={labelSelectable ? 'select-all' : undefined}>
                                {labelDescription}
                            </Description>
                        </div>
                    ) : null}
                </div>
            ) : null}
            <div
                ref={wrapperRef}
                className={clsx(
                    'flex flex-1 flex-wrap items-center rounded-lg border border-base px-2 transition',
                    value.length > 0 ? 'py-[1px]' : 'py-[5px]',
                    {
                        'focus-ring': focused,
                        'border-danger': feedback && feedback.type === 'danger',
                    }
                )}
            >
                {value.map((tag, index) => (
                    <Chip
                        key={index}
                        onDelete={() => onRemoveTag(index)}
                        size="small"
                        className="m-1"
                    >
                        {renderTag(tag)}
                    </Chip>
                ))}
                <input
                    ref={ref}
                    {...inputProps}
                    value={text}
                    onChange={(event) => onChangeText(event.target.value)}
                    placeholder={!value.length ? placeholder : undefined}
                    className="h-5 flex-1 bg-transparent placeholder:text-placeholder"
                    onKeyDown={onKeyDown}
                    onFocus={(event) => {
                        if (disabled) {
                            return;
                        }
                        setFocused(true);
                        if (inputProps.onFocus) {
                            inputProps.onFocus(event);
                        }
                    }}
                    onBlur={(event) => {
                        if (disabled) {
                            return;
                        }
                        setFocused(false);
                        if (inputProps.onBlur) {
                            inputProps.onBlur(event);
                        }
                    }}
                />
                {FeedbackIcon ? (
                    <FeedbackIcon className={clsx('mr-4', feedbackStyle)} size={18} />
                ) : null}
                {button ? <div className="ml-auto">{button}</div> : null}
            </div>
            {feedback && typeof feedback.message !== 'undefined' && (
                <div className={clsx('pt-2 text-ui-small', feedbackStyle)}>{feedback.message}</div>
            )}
        </div>
    );
}
